import React, { useEffect, useState } from 'react';
import { reportsAPI } from '../services/api';
import { format } from 'date-fns';
import { Send, Calendar, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

export default function ReportsPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    reportsAPI.dashboard()
      .then(r => setReport(r.data))
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, []);

  const sendReport = async () => {
    setSending(true);
    try {
      await reportsAPI.sendDaily();
      toast.success('Daily report emailed to management!');
    } catch { toast.error('Failed to send report'); }
    setSending(false);
  };

  if (loading) return <div className="text-center py-20 text-gray-400">Loading report...</div>;

  const s = report?.status_summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500">Daily management report — {format(new Date(), 'dd MMMM yyyy')}</p>
        </div>
        {['admin'].includes(user?.role) && (
          <button onClick={sendReport} disabled={sending}
            className="flex items-center gap-2 bg-blue-950 hover:bg-blue-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60">
            <Send size={15} />
            {sending ? 'Sending...' : 'Email Report to Management'}
          </button>
        )}
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Cases', value: s?.total, color: 'bg-blue-950' },
          { label: 'Active', value: s?.active, color: 'bg-green-600' },
          { label: 'Adjourned', value: s?.adjourned, color: 'bg-yellow-500' },
          { label: 'Disposed', value: s?.disposed, color: 'bg-gray-500' },
          { label: 'Hearings Today', value: s?.hearings_today, color: 'bg-red-500' },
          { label: 'New Today', value: s?.new_today, color: 'bg-purple-600' },
          { label: 'Updated Today', value: s?.updated_today, color: 'bg-indigo-600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-3 h-10 rounded-full ${color} shrink-0`} />
            <div>
              <p className="text-xl font-bold text-gray-800">{value ?? '—'}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Upcoming Hearings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center gap-2">
          <Calendar size={17} className="text-blue-900" />
          <h2 className="font-semibold text-gray-800">Upcoming Hearings — Next 7 Days ({report?.upcoming_hearings?.length})</h2>
        </div>
        <div className="divide-y divide-gray-50">
          {report?.upcoming_hearings?.length === 0 && (
            <p className="text-center text-gray-400 py-10 text-sm">No upcoming hearings</p>
          )}
          {report?.upcoming_hearings?.map((h, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5">
              <div className="text-center min-w-[44px] bg-blue-950 text-white rounded-lg py-1.5">
                <p className="text-sm font-bold">{format(new Date(h.next_hearing_date), 'dd')}</p>
                <p className="text-xs opacity-75">{format(new Date(h.next_hearing_date), 'MMM')}</p>
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm text-gray-800">{h.case_number} — {h.title}</p>
                <p className="text-xs text-gray-400">
                  {h.court_name} · {h.next_hearing_type || 'General'} · Lawyer: {h.assigned_lawyer__first_name} {h.assigned_lawyer__last_name}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lawyer Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center gap-2">
          <Users size={17} className="text-blue-900" />
          <h2 className="font-semibold text-gray-800">Lawyer-wise Case Summary</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Lawyer', 'Active', 'Adjourned', 'Disposed', 'Total'].map(h => (
                <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {report?.lawyer_stats?.map((l, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-3 font-medium text-gray-800">{l.name}</td>
                <td className="px-5 py-3 text-green-700 font-semibold">{l.active}</td>
                <td className="px-5 py-3 text-yellow-700 font-semibold">{l.adjourned}</td>
                <td className="px-5 py-3 text-gray-600 font-semibold">{l.disposed}</td>
                <td className="px-5 py-3 text-blue-900 font-bold">{l.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
